import Head from "next/head";
import Link from "next/link";
import { AdminLayout } from "components/admin/admin-layout";
import { gql } from "@apollo/client";
import { useGetAllProductsQuery } from "lib/graphql-generated";
import { Button } from "components/buttons/button";
import { useRouter } from "next/router";
import currency from "currency.js";
import { parseParam } from "lib/utils";
import querystring from "querystring";
import { useInput } from "lib/hooks/use-input";
import { AdminSearchBar } from "components/admin/admin-search-bar";

const GET_PRODUCTS = gql`
  query GetAllProducts(
    $limit: Int
    $offset: Int
    $conditions: products_bool_exp
  ) {
    products_aggregate(where: $conditions) {
      aggregate {
        count
      }
    }

    products(limit: $limit, offset: $offset, where: $conditions) {
      id
      title
      price
      stock
    }
  }
`;

export const Products = () => {
  /**
   * Query Strings
   */
  const router = useRouter();
  const pQuery = parseInt(router.query["p"] as string) || 0;
  const statusQuery = parseParam(router.query["status"]);
  const searchQuery = parseParam(router.query["search"]) || "";
  const paginate = Boolean(pQuery) ? pQuery : 0;
  const limit = 25;
  const offset = Boolean(paginate) ? paginate * limit : 0;
  const search = useInput<string>(searchQuery);

  const getPathWithQuery = ({
    p,
    search,
  }: {
    p?: number;
    search?: string;
  } = {}) => {
    const queryString = querystring.encode({
      p: p ?? paginate ?? 0,
      search: search ?? searchQuery ?? "",
    });
    return `${router.pathname}?${queryString}`;
  };

  const { data, loading, error } = useGetAllProductsQuery({
    variables: {
      limit: limit,
      offset: offset,
      conditions: {
        title: { _ilike: `%${searchQuery}%` },
      },
    },
  });

  return (
    <>
      <Head>
        <title>Admin | Products</title>
      </Head>
      <AdminLayout
        page='Products'
        searchBar={
          <AdminSearchBar
            placeholder='Search by product title'
            onSearch={() =>
              router.replace(getPathWithQuery({ search: search.value, p: 0 }))
            }
            {...search.bind}
          />
        }>
        <article className='space-y-4'>
          <header className='flex flex-col xl:flex-row flex-wrap w-full justify-center space-y-4 xl:space-y-0 xl:space-x-4'>
            <h1 className='header text-4xl ml-auto mr-auto xl:ml-0 xl:mr-auto'>
              Products
            </h1>
            <Button
              buttonType='secondary'
              buttonSize='lg'
              href='/admin/products/new'
              className='my-auto ml-auto mr-auto'>
              <svg
                className='w-4 h-4 mr-2 -ml-1 mb-1'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
              <span>New Product</span>
            </Button>
          </header>

          <table className='min-w-full divide-y divide-gray-200 shadow overflow-hidden border-b border-gray-200 sm:rounded-lg'>
            <thead className=' bg-gray-100 text-left text-xs leading-4 font-medium text-gray-700 uppercase tracking-wider'>
              <tr>
                <th className='px-6 py-3'>Title</th>
                <th className='px-6 py-3'>Stock</th>
                <th className='px-6 py-3 hidden xl:table-cell'>Price</th>
                <th className='px-6 py-3 hidden xl:table-cell'>Type</th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200 text-sm text-gray-800'>
              {loading ? (
                <tr className='hover:bg-gray-300'>
                  <td
                    className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                    colSpan={4}>
                    Loading Data...
                  </td>
                </tr>
              ) : data.products.length !== 0 ? (
                data?.products.map((product) => (
                  <tr
                    key={product.id}
                    className='hover:bg-gray-300 cursor-pointer'
                    onClick={() =>
                      router.push(`/admin/products/${product.id}`)
                    }>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      {product.title}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      {product.stock}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5 hidden xl:table-cell'>
                      {currency(product.price, { fromCents: true }).value}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5 hidden xl:table-cell'>
                      <span
                        className={`rounded-full border px-2 py-0.5 border-green-default text-green-dark`}>
                        Product
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className='hover:bg-gray-300'>
                  <td
                    className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                    colSpan={4}>
                    Could not find products...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </article>
        {data && (
          <nav
            className='py-3 flex items-center justify-between border-t border-gray-200 '
            aria-label='Pagination'>
            <div className='hidden sm:block'>
              <p className='text-sm text-gray-700'>
                Showing{" "}
                <span className='font-medium'>
                  {offset}
                  {` `}
                </span>
                to{" "}
                <span className='font-medium'>
                  {offset + limit}
                  {` `}
                </span>
                of{" "}
                <span className='font-medium'>
                  {data?.products_aggregate.aggregate.count}
                  {` `}
                </span>
                results
              </p>
            </div>
            <div className='flex-1 flex justify-between sm:justify-end'>
              {paginate !== 0 && (
                <Link href={getPathWithQuery({ p: paginate - 1 })}>
                  <a className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                    Previous
                  </a>
                </Link>
              )}
              {paginate !==
                Math.floor(data.products_aggregate.aggregate.count / 25) && (
                <Link href={getPathWithQuery({ p: paginate + 1 })}>
                  <a className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                    Next
                  </a>
                </Link>
              )}
            </div>
          </nav>
        )}
      </AdminLayout>
    </>
  );
};

export default Products;
